export type AxisRange = { min: number; max: number; step: number };
export type AxisRegion = { left: number; top: number; width: number; height: number };
export type AxisConfig = { domain: AxisRange; range: AxisRange };

const LABEL_FONT = "12px sans-serif";
const TICK_LIMIT = 300;

export function formatAxisTick(value: number): string {
  if (!Number.isFinite(value)) return "";
  const rounded = Number(value.toPrecision(3));
  if (rounded === 0 || Object.is(rounded, -0)) return "0";
  const magnitude = Math.abs(rounded);
  if (magnitude >= 1_000_000 || magnitude < 0.001) {
    const [mantissa, exponent] = rounded.toExponential(2).split("e");
    return `${Number(mantissa)}e${Number(exponent)}`;
  }
  return String(rounded);
}

export function generateAxisTicks({ min, max, step }: AxisRange): number[] {
  if (![min, max, step].every(Number.isFinite) || step <= 0 || max < min) return [];
  const count = Math.floor((max - min) / step + 1e-10) + 1;
  if (!Number.isSafeInteger(count) || count > TICK_LIMIT) {
    throw new RangeError(`Axis settings produce more than ${TICK_LIMIT} ticks.`);
  }
  const floatingPointTolerance = Number.EPSILON * Math.max(Math.abs(min), Math.abs(max), Math.abs(step), Number.MIN_VALUE) * 8;
  const tolerance = Math.max(Math.abs(step) * 1e-9, floatingPointTolerance);
  const ticks: number[] = [];
  for (let index = 0; index < count; index += 1) {
    const rawTick = min + index * step;
    // Decimal steps such as -1.2 + 4 * 0.3 can leave a tiny residue instead
    // of zero. Snap only within floating-point error at this range's scale;
    // genuine small values remain intact and are formatted normally.
    const tick = Math.abs(rawTick) <= floatingPointTolerance ? 0 : rawTick;
    if (tick > max + tolerance) break;
    ticks.push(Object.is(tick, -0) ? 0 : tick);
  }
  return ticks;
}

export function drawCoordinateAxes(ctx: CanvasRenderingContext2D, config: AxisConfig, region: AxisRegion) {
  const xTicks = generateAxisTicks(config.domain);
  const yTicks = generateAxisTicks(config.range);
  if (!xTicks.length || !yTicks.length || region.width <= 0 || region.height <= 0) return;

  ctx.save();
  ctx.font = LABEL_FONT;
  ctx.textBaseline = "middle";
  const xLabelHalfWidth = Math.max(...xTicks.map((tick) => ctx.measureText(formatAxisTick(tick)).width / 2));
  const yLabelWidth = Math.max(...yTicks.map((tick) => ctx.measureText(formatAxisTick(tick)).width));
  const yAxisInitiallyAtLeft = config.domain.min > 0;
  const commonXInset = Math.max(9, xLabelHalfWidth + 4);
  let labelSide: "left" | "right" = yAxisInitiallyAtLeft ? "right" : "left";
  let leftInset = Math.max(commonXInset, labelSide === "left" ? yLabelWidth + 9 : 8);
  let rightInset = Math.max(commonXInset, labelSide === "right" ? yLabelWidth + 9 : 8);

  const fractionX = (value: number) => (value - config.domain.min) / (config.domain.max - config.domain.min);
  const candidatePlotWidth = Math.max(1, region.width - leftInset - rightInset);
  const candidateZeroX = region.left + leftInset + fractionX(0) * candidatePlotWidth;
  if (config.domain.min <= 0 && config.domain.max >= 0 && candidateZeroX - (region.left + leftInset) < yLabelWidth + 9) {
    labelSide = "right";
    leftInset = commonXInset;
    rightInset = Math.max(commonXInset, yLabelWidth + 9);
  }

  const topInset = 9;
  const bottomInset = 23;
  const plotLeft = region.left + leftInset;
  const plotRight = region.left + region.width - rightInset;
  const plotTop = region.top + topInset;
  const plotBottom = region.top + region.height - bottomInset;
  if (plotRight <= plotLeft || plotBottom <= plotTop) {
    ctx.restore();
    return;
  }

  const mapX = (value: number) => plotLeft + fractionX(value) * (plotRight - plotLeft);
  const mapY = (value: number) => plotBottom - ((value - config.range.min) / (config.range.max - config.range.min)) * (plotBottom - plotTop);
  const hasZeroX = config.domain.min <= 0 && config.domain.max >= 0;
  const hasZeroY = config.range.min <= 0 && config.range.max >= 0;
  const xAxisX = hasZeroX ? mapX(0) : config.domain.min > 0 ? plotLeft : plotRight;
  const xAxisY = hasZeroY ? mapY(0) : config.range.min > 0 ? plotBottom : plotTop;

  ctx.globalCompositeOperation = "source-over";
  ctx.fillStyle = "rgba(15,23,42,0.25)";
  for (const x of xTicks) {
    const px = mapX(x);
    for (const y of yTicks) {
      ctx.beginPath();
      ctx.arc(px, mapY(y), 1.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  ctx.strokeStyle = "rgba(15,23,42,0.6)";
  ctx.lineWidth = 2;
  ctx.fillStyle = "#0f172a";
  ctx.textBaseline = "top";
  ctx.textAlign = "center";
  const seenX = new Set<string>();
  let previousXRight = Number.NEGATIVE_INFINITY;
  const xTextY = Math.min(region.top + region.height - 14, xAxisY + 7);
  for (const value of xTicks) {
    const label = formatAxisTick(value);
    if (seenX.has(label)) continue;
    const px = mapX(value);
    const labelWidth = ctx.measureText(label).width;
    const left = px - labelWidth / 2;
    if (left < region.left + 1 || left + labelWidth > region.left + region.width - 1 || left < previousXRight + 3) continue;
    if (label === "0" && hasZeroX && hasZeroY) {
      seenX.add(label);
      continue;
    }
    seenX.add(label);
    previousXRight = left + labelWidth;
    ctx.beginPath();
    ctx.moveTo(px, xAxisY - 3);
    ctx.lineTo(px, xAxisY + 3);
    ctx.stroke();
    ctx.fillText(label, px, xTextY);
  }

  ctx.textBaseline = "middle";
  ctx.textAlign = labelSide === "left" ? "right" : "left";
  const yLabelX = labelSide === "left" ? xAxisX - 8 : xAxisX + 8;
  const seenY = new Set<string>();
  let previousY = Number.POSITIVE_INFINITY;
  for (const value of yTicks) {
    const label = formatAxisTick(value);
    if (seenY.has(label) || (label === "0" && hasZeroX && hasZeroY)) continue;
    const py = mapY(value);
    if (py < region.top + 7 || py > region.top + region.height - 7 || previousY - py < 15) continue;
    seenY.add(label);
    previousY = py;
    ctx.beginPath();
    ctx.moveTo(xAxisX - 3, py);
    ctx.lineTo(xAxisX + 3, py);
    ctx.stroke();
    ctx.fillText(label, yLabelX, py);
  }

  // Draw true zero axes only when zero is inside the selected domain/range.
  if (hasZeroX) {
    ctx.beginPath();
    ctx.moveTo(xAxisX, plotTop);
    ctx.lineTo(xAxisX, plotBottom);
    ctx.stroke();
  }
  if (hasZeroY) {
    ctx.beginPath();
    ctx.moveTo(plotLeft, xAxisY);
    ctx.lineTo(plotRight, xAxisY);
    ctx.stroke();
  }
  if (hasZeroX && hasZeroY) {
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText("0", Math.min(region.left + region.width - 8, xAxisX + 6), Math.min(region.top + region.height - 14, xAxisY + 7));
  }
  ctx.restore();
}
